import React from 'react';
import { TrendingUp, FileText, ChevronDown } from 'lucide-react';
import { ProgressNoteCard } from '../../molecules/ProgressNoteCard';
import { ProgressNotesResponse } from '../../../types/residents';
import type { AiFeedbackItem, AiFeedbackSentiment } from '../../../types/residents';
import { formatDateTime } from '../../../utils/dateFormat';

const INSIGHTS_DAY_OPTIONS = [3, 5, 7, 14, 21, 30];

interface ProgressNotesSectionProps {
  data: ProgressNotesResponse | null;
  isLoading: boolean;
  error: Error | null;
  days: number;
  onDaysChange: (days: number) => void;
  residentId?: string;
  riskDocumentId?: string;
  wqId?: string;
  feedbackItems?: AiFeedbackItem[];
  insightsDays?: number;
  /** UI-visibility-only switch — reasoning is still generated/stored when false. */
  reasoningVisible?: boolean;
}

export const ProgressNotesSection: React.FC<ProgressNotesSectionProps> = ({
  data,
  isLoading,
  error,
  days,
  onDaysChange,
  residentId,
  riskDocumentId,
  wqId,
  feedbackItems = [],
  insightsDays,
  reasoningVisible = false,
}) => {
  const lookupFeedback = (
    contentType: string,
    sourceItemId?: string
  ): { sentiment: AiFeedbackSentiment; comment?: string | null } | null => {
    const match = feedbackItems.find(
      (f) =>
        f.content_type === contentType &&
        (sourceItemId ? f.source_item_id === sourceItemId : !f.source_item_id)
    );
    return match ? { sentiment: match.sentiment, comment: match.comment } : null;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading progress notes...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading progress notes: {error.message}
        </div>
      );
    }

    if (!data || data.notes.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No progress notes available
        </div>
      );
    }

    return (
      <div>
        {data.notes.map((note) => (
          <ProgressNoteCard
            key={note.id || note.date}
            dateTime={formatDateTime(note.time ? `${note.date}T${note.time}` : note.date)}
            author={note.author}
            credentials={note.credentials ?? ""}
            riskTags={note.risk_tags}
            content={note.content}
            careplanStatus={note.in_care_plan ? 'in-care-plan' : 'not-planned'}
            reasoning={note.reasoning}
            reasoningVisible={reasoningVisible}
            noteId={note.id ?? undefined}
            residentId={residentId}
            riskDocumentId={riskDocumentId}
            wqId={wqId}
            insightsDays={insightsDays}
            existingReasoningFeedback={lookupFeedback("note_reasoning", note.id ?? undefined)?.sentiment}
            existingReasoningComment={lookupFeedback("note_reasoning", note.id ?? undefined)?.comment}
            existingClassificationFeedback={lookupFeedback("note_risk_classification", note.id ?? undefined)?.sentiment}
            existingClassificationComment={lookupFeedback("note_risk_classification", note.id ?? undefined)?.comment}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="mb-6">
      {/* Main Header */}
      <div className="flex items-center gap-3 mb-4">
        <TrendingUp size={20} className="text-[#228189]" />
        <h2 className="text-lg font-bold text-gray-900">New Insights</h2>
        <div className="relative ml-1">
          <select
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            className="appearance-none pl-3 pr-7 py-1 border border-solid border-[#228189]/30 rounded-full text-xs font-semibold text-[#228189] bg-[#228189]/5 cursor-pointer hover:bg-[#228189]/10 transition-colors focus:outline-none focus:ring-1 focus:ring-[#228189]/40"
          >
            {INSIGHTS_DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>Last {d} Days</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#228189] pointer-events-none" />
        </div>
      </div>

      {/* Sub-Header */}
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-gray-600" />
        <h3 className="text-base font-semibold text-gray-700">
          Progress Notes Indicating Risk
        </h3>
      </div>

      {renderContent()}
    </div>
  );
};
